/**
 * Inbound website leads — the B2B contact form and the resource download gate.
 *
 * These used to POST to Medusa's `prospecting` module (`POST /prospecting/contact`),
 * which wrote two Postgres rows nobody ever read and emitted an event whose only
 * subscriber posted a Slack message to #sales. With Medusa being decommissioned
 * (MIN-2931, since done), MIN-5065 moved the lead path to `POST /api/contact` in this app.
 * The Slack notification is the part that was ever load-bearing, so that is what
 * was preserved; the write-only Postgres rows were not.
 *
 * This module holds the parts that must be identical on both sides of the wire:
 * the lead shape, the validation predicate, and the Slack rendering. The route
 * handler is what actually enforces — a browser is not a trusted validator, and
 * the endpoint is public — but the client runs the same predicate so the two
 * verdicts cannot drift and a visitor gets a field error instead of a 400.
 */

export type ContactLead = {
  /** Organisation / store the enquiry is about. Free text from the visitor. */
  storeName: string
  email: string
  name: string
  mobile?: string
  message?: string
  /** Which surface produced the lead — for the Slack line, not for routing. */
  source: ContactLeadSource
}

export const CONTACT_LEAD_SOURCES = ["contact_form", "download_gate"] as const
export type ContactLeadSource = (typeof CONTACT_LEAD_SOURCES)[number]

/** Caps mirror the old Medusa validator's intent: free text, but not unbounded. */
export const CONTACT_LEAD_LIMITS = {
  storeName: 200,
  email: 320, // RFC 5321 maximum path length
  name: 200,
  mobile: 50,
  message: 5000,
} as const

export type ContactLeadValidation =
  | { ok: true; lead: ContactLead }
  | { ok: false; errors: string[] }

/**
 * `a@b` is deliberately all this checks beyond "looks like an address".
 *
 * A stricter regex rejects addresses that are legal and deliverable (quoted
 * local parts, new gTLDs, unicode domains) and the only cost of letting a bad
 * one through is a Slack message somebody cannot reply to — whereas rejecting a
 * real customer's address loses the lead outright. The old Medusa validator used
 * zod's `.email()`, which is about this permissive.
 */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * The one definition of "looks like an email", exported so the form fields use
 * it too.
 *
 * The field components used to carry their own `/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i`,
 * which is ASCII-only and therefore rejects addresses this module deliberately
 * accepts — a visitor on a unicode domain was blocked by the UI before the
 * lenient server check ever ran, and the lead was lost at the keyboard.
 */
export function isValidEmailShape(value: string): boolean {
  return EMAIL_SHAPE.test(value.trim())
}

/** Characters that can legitimately appear in a typed phone number. */
const MOBILE_ALLOWED_CHARS = /^[+]?[\d\s\-().]*$/
const MOBILE_MIN_DIGITS = 8
const MOBILE_MAX_DIGITS = 15

/**
 * Phone-number shape, shared with `ContactMobileField`.
 *
 * *Whether* a phone number is required is a per-form choice configured in Sanity
 * and stays a UI concern — the server cannot know which form a lead came from.
 * The *format* is not: it lived only in the field component, so a direct caller
 * could post any 50 characters and have them rendered into #sales as a phone
 * number. Both sides now agree, and because the UI is the stricter of the two on
 * requiredness, nothing that passes the form can fail here.
 */
export function isValidMobileShape(value: string): boolean {
  const trimmed = value.trim()
  if (!trimmed) return false
  if (!MOBILE_ALLOWED_CHARS.test(trimmed)) return false
  const digits = trimmed.replace(/\D/g, "")
  return digits.length >= MOBILE_MIN_DIGITS && digits.length <= MOBILE_MAX_DIGITS
}

function asTrimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

/**
 * The single source of truth for "is this lead submittable".
 *
 * Returns the normalised lead (trimmed, empty optionals dropped) rather than
 * mutating its input, and returns the reasons rather than logging them — the
 * caller decides whether a rejection is a visitor-facing field error or a
 * server-side warn.
 */
export function validateContactLead(input: unknown): ContactLeadValidation {
  if (input === null || typeof input !== "object") {
    return { ok: false, errors: ["Request body must be an object"] }
  }

  const raw = input as Record<string, unknown>
  const errors: string[] = []

  const storeName = asTrimmedString(raw.storeName)
  const email = asTrimmedString(raw.email)
  const name = asTrimmedString(raw.name)
  const mobile = asTrimmedString(raw.mobile)
  const message = asTrimmedString(raw.message)

  if (!storeName) errors.push("storeName is required")
  else if (storeName.length > CONTACT_LEAD_LIMITS.storeName) errors.push("storeName is too long")

  if (!email) errors.push("email is required")
  else if (email.length > CONTACT_LEAD_LIMITS.email) errors.push("email is too long")
  else if (!isValidEmailShape(email)) errors.push("email is not a valid address")

  if (!name) errors.push("name is required")
  else if (name.length > CONTACT_LEAD_LIMITS.name) errors.push("name is too long")

  if (mobile && mobile.length > CONTACT_LEAD_LIMITS.mobile) errors.push("mobile is too long")
  else if (mobile && !isValidMobileShape(mobile)) errors.push("mobile is not a valid phone number")
  if (message && message.length > CONTACT_LEAD_LIMITS.message) errors.push("message is too long")

  const source = raw.source
  if (!CONTACT_LEAD_SOURCES.includes(source as ContactLeadSource)) {
    errors.push("source must be one of: " + CONTACT_LEAD_SOURCES.join(", "))
  }

  if (errors.length > 0) return { ok: false, errors }

  return {
    ok: true,
    lead: {
      storeName,
      email,
      name,
      ...(mobile ? { mobile } : {}),
      ...(message ? { message } : {}),
      source: source as ContactLeadSource,
    },
  }
}

/**
 * Escape the three characters Slack treats as markup control characters.
 *
 * Everything here is attacker-controlled text from a public, unauthenticated
 * form, and Slack would otherwise read `<https://evil|Click me>` in a message
 * body as a link with arbitrary display text. Slack specifies these three and
 * only these three, and they must be escaped before any other interpolation.
 *
 * @see https://api.slack.com/reference/surfaces/formatting#escaping
 */
export function escapeSlackText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

const SOURCE_LABELS: Record<ContactLeadSource, string> = {
  contact_form: "Contact form",
  download_gate: "Download gate",
}

/**
 * Render the Slack message body.
 *
 * Deliberately plain `text` rather than Block Kit: this is read by a human in
 * #sales who then replies by email, and plain text is what the Medusa
 * subscriber sent, so nothing about the channel changes.
 */
export function formatContactLeadForSlack(lead: ContactLead): string {
  const lines = [
    `*New ${SOURCE_LABELS[lead.source].toLowerCase()} enquiry — ${escapeSlackText(lead.storeName)}*`,
    `*Contact:* ${escapeSlackText(lead.name)} (${escapeSlackText(lead.email)})`,
  ]
  if (lead.mobile) lines.push(`*Phone:* ${escapeSlackText(lead.mobile)}`)
  if (lead.message) lines.push(`*Message:* ${escapeSlackText(lead.message)}`)
  lines.push(`_via ${SOURCE_LABELS[lead.source]}_`)
  return lines.join("\n")
}

/**
 * Longest a single field may be when written into a log line. Generous enough to
 * keep a real enquiry readable and act on it, short enough that one submission
 * cannot dominate a log stream.
 */
const LOG_FIELD_MAX = 500

/**
 * Make an attacker-controlled string safe to interpolate into a log line.
 *
 * `/api/contact` is public and unauthenticated, and its failure path logs the
 * whole lead so it can be recovered by hand. That is a log-injection sink: a
 * newline in the `message` field would let a stranger forge an entirely
 * synthetic-looking log entry, and a 5000-character message would let them bury
 * real ones.
 *
 * `JSON.stringify` alone already escapes CR/LF, but it is an implicit sanitiser
 * that a reader (and CodeQL) has to take on trust, and it leaves U+2028/U+2029
 * — line terminators to some consumers — intact and the length unbounded. This
 * replaces every C0/C1 control character and both unicode line separators, then
 * truncates, so the result is provably one bounded line.
 */
export function sanitiseForLog(value: string): string {
  // Line breaks are stripped by their own literal pattern FIRST, before the
  // broader control-character sweep below. Defensively redundant — the sweep's
  // \u0000-\u001F range already covers CR and LF — but CodeQL's js/log-injection
  // sanitiser detection matches on a `replace` whose regex visibly matches a line
  // break, and it does not expand a codepoint range to see one. Written this way,
  // the guarantee is legible to the scanner as well as to a reader.
  const singleLine = value.replace(/[\r\n]/g, " ").replace(/[\u2028\u2029]/g, " ")
  // eslint-disable-next-line no-control-regex
  const flattened = singleLine.replace(/[\u0000-\u001F\u007F-\u009F]/g, " ")
  return flattened.length > LOG_FIELD_MAX ? `${flattened.slice(0, LOG_FIELD_MAX)}…[truncated]` : flattened
}

export type SubmitContactLeadResult = { ok: true } | { ok: false; errors: string[] }

/**
 * Client-side submit. Runs the shared predicate first so an invalid form is
 * caught without a round trip, then POSTs to the route that actually enforces.
 */
export async function submitContactLead(lead: ContactLead): Promise<SubmitContactLeadResult> {
  const validation = validateContactLead(lead)
  if (!validation.ok) return { ok: false, errors: validation.errors }

  const res = await fetch("/api/contact", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(validation.lead),
  })

  if (res.ok) return { ok: true }

  // A non-2xx means the lead did NOT reach anyone. Surface it rather than
  // letting the caller show a success state for a lead that was dropped.
  const detail = await res
    .json()
    .then((body: { errors?: unknown }) => (Array.isArray(body?.errors) ? (body.errors as string[]) : []))
    .catch(() => [])

  return { ok: false, errors: detail.length > 0 ? detail : [`Request failed with status ${res.status}`] }
}
